# Cookie Policy

**Effective Date:** January 15, 2026
**Last Updated:** January 15, 2026

This Cookie Policy explains how FHIRfly.io LLC ("FHIRfly," "we," "us," or "our") uses cookies and similar technologies on our website and dashboard at fhirfly.io (the "Site").

---

## 1. What Are Cookies?

Cookies are small text files stored on your device when you visit a website. They help websites remember information about your visit, making your next visit easier and the site more useful.

Similar technologies include:

- **Local Storage:** Data stored in your browser
- **Session Storage:** Temporary data cleared when you close your browser
- **Pixels/Beacons:** Small images that track page views and interactions

---

## 2. How We Use Cookies

### 2.1 Essential Cookies

These cookies are necessary for the Site to function properly. While you can block them in your browser settings, doing so will prevent the dashboard and authenticated features from working.

| Cookie Name | Purpose | Duration |
|-------------|---------|----------|
| `fhirfly_session` | Authentication session | Session |
| `fhirfly_refresh` | Token refresh | 30 days |
| `fhirfly_org` | Current organization context | Session |

**Legal Basis:** Necessary for the performance of our contract with you.

### 2.2 Functional Cookies

These cookies remember your preferences and settings.

| Cookie Name | Purpose | Duration |
|-------------|---------|----------|
| `theme` | Dark/light mode preference | 1 year |
| `sidebar_collapsed` | Dashboard sidebar state | 1 year |

**Legal Basis:** Your consent (implied through use) or legitimate interest in providing a better experience.

### 2.3 Analytics Cookies

We use analytics to understand how visitors use our Site and to improve it.

| Cookie Name | Provider | Purpose | Duration |
|-------------|----------|---------|----------|
| `_ga` | Google Analytics | Distinguishes users | 2 years |
| `_ga_*` | Google Analytics | Maintains session state | 2 years |

**Data Collected:**
- Pages visited
- Time spent on pages
- Referral sources
- Browser and device information
- Geographic location (country/region level)

**Legal Basis:** Your consent or our legitimate interest in improving the Service.

### 2.4 Cookie Consent (EU/UK Users)

If you are located in the European Union or United Kingdom, we obtain your consent for non-essential cookies (functional and analytics) through your continued use of the Site after being informed of our cookie practices. You may withdraw consent at any time by adjusting your browser settings to block cookies or by using the opt-out tools listed in Section 5.

We are working to implement a cookie consent banner for more granular control. Until then, please manage your preferences through browser settings.

---

## 3. Third-Party Cookies

We use limited third-party services that may set their own cookies:

### 3.1 Google Analytics

We use Google Analytics to analyze website traffic and understand how visitors use our Site. Google processes this data in accordance with its privacy policy.

- [Google's Privacy Policy](https://policies.google.com/privacy)
- [How Google uses data](https://policies.google.com/technologies/partner-sites)
- [Opt out of Google Analytics](https://tools.google.com/dlpage/gaoptout)

### 3.2 Stripe

When you access billing or payment features, Stripe may set cookies for fraud prevention and payment processing.

- [Stripe's Privacy Policy](https://stripe.com/privacy)
- [Stripe's Cookie Policy](https://stripe.com/cookies-policy/legal)

---

## 4. Cookie Duration

| Type | Description |
|------|-------------|
| **Session Cookies** | Deleted when you close your browser |
| **Persistent Cookies** | Remain on your device for a set period or until you delete them |

---

## 5. Managing Cookies

### 5.1 Browser Settings

Most browsers allow you to control cookies through their settings:

- **Chrome:** Settings > Privacy and Security > Cookies
- **Firefox:** Options > Privacy & Security > Cookies
- **Safari:** Preferences > Privacy > Cookies
- **Edge:** Settings > Privacy, Search, and Services > Cookies

### 5.2 Opt-Out Tools

- **Google Analytics:** [Browser Add-on](https://tools.google.com/dlpage/gaoptout)
- **General Opt-out:** [Your Ad Choices](https://optout.aboutads.info/)

### 5.3 Impact of Disabling Cookies

If you disable cookies:

| Cookie Type | Impact if Disabled |
|-------------|-------------------|
| Essential | Cannot use the dashboard or API management features |
| Functional | Preferences won't be saved between visits |
| Analytics | No impact on functionality |

---

## 6. Do Not Track

Our Site does not currently respond to "Do Not Track" (DNT) browser signals. This is because there is no industry standard for DNT implementation.

---

## 7. Updates to This Policy

We may update this Cookie Policy to reflect changes in our practices or for legal compliance. Updates will be posted on this page with a new "Last Updated" date.

---

## 8. Contact Us

For questions about our use of cookies, contact us:

**FHIRfly.io LLC**
Email: admin@fhirfly.io
Phone: (816) 552-2628
Address: 30 N Gould St, Ste 60120, Sheridan, WY 82801
